import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, ArrowLeft } from 'react-feather';
import './LegalPages.css';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page-container">
      <div className="legal-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <button className="home-button" onClick={() => navigate('/')}>
          <Home size={20} />
          <span>Home</span>
        </button>
      </div>

      <div className="legal-page-content">
        <div className="legal-page-title">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: January 2025</p>
        </div>

        <div className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            Welcome to Brainy AI. We are committed to protecting your privacy and ensuring the security
            of your personal information. This Privacy Policy explains how we collect, use, disclose,
            and safeguard your information when you use our AI-powered educational platform.
          </p>
          <p>
            This policy applies to information we collect through our website, mobile applications, and
            any related services (collectively, the "Service"). By using our Service, you consent to
            the data practices described in this policy.
          </p>
          <p>
            We comply with applicable data protection laws, including the General Data Protection
            Regulation (GDPR), the UK Data Protection Act 2018, and the California Consumer Privacy
            Act (CCPA).
          </p>
        </div>

        <div className="legal-section">
          <h2>2. Information We Collect</h2>
          <h3>Information You Provide Directly</h3>
          <p>
            We collect information that you voluntarily provide when using our Service:
          </p>
          <ul>
            <li>
              <strong>Account Information:</strong> Username, email address, password (encrypted),
              and profile details
            </li>
            <li>
              <strong>Educational Data:</strong> Learning goals, progress, completed lessons,
              assessment results, and notes
            </li>
            <li>
              <strong>User Content:</strong> Text, files, and other content you create, upload,
              or submit through the Service
            </li>
            <li>
              <strong>Communication Data:</strong> Messages you send to our support team or
              through chat features
            </li>
            <li>
              <strong>Payment Information:</strong> Billing details processed securely through
              third-party payment processors (we do not store complete credit card numbers)
            </li>
          </ul>

          <h3>Information Collected Automatically</h3>
          <p>
            When you use our Service, we automatically collect certain information:
          </p>
          <ul>
            <li>
              <strong>Usage Data:</strong> Pages viewed, features used, time spent, clicks,
              and interaction patterns
            </li>
            <li>
              <strong>Device Information:</strong> Device type, operating system, browser type,
              IP address, and unique device identifiers
            </li>
            <li>
              <strong>Location Data:</strong> General location based on IP address (not precise
              GPS location unless you explicitly provide it)
            </li>
            <li>
              <strong>Cookies and Similar Technologies:</strong> Small data files stored on
              your device (see our Cookie Policy section below)
            </li>
            <li>
              <strong>Analytics Data:</strong> Performance metrics and error reports to improve
              Service quality
            </li>
          </ul>

          <h3>Information from Third Parties</h3>
          <p>
            We may receive information about you from:
          </p>
          <ul>
            <li>Authentication services if you sign in using third-party accounts</li>
            <li>Payment processors regarding transaction status</li>
            <li>Analytics providers to understand Service usage patterns</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>3. How We Use Your Information</h2>
          <p>
            We use the information we collect for the following purposes:
          </p>

          <h3>Providing and Improving the Service</h3>
          <ul>
            <li>Create and manage your account</li>
            <li>Deliver personalized educational content and recommendations</li>
            <li>Generate AI-powered lessons, assessments, and learning paths</li>
            <li>Track your learning progress and achievements</li>
            <li>Process payments and manage subscriptions</li>
            <li>Provide customer support and respond to inquiries</li>
            <li>Improve and optimize the Service based on usage patterns</li>
          </ul>

          <h3>Training and Improving AI Models</h3>
          <ul>
            <li>Train and refine our AI models using anonymized and aggregated data</li>
            <li>Improve the accuracy and relevance of AI-generated content</li>
            <li>Develop new features and educational capabilities</li>
          </ul>

          <h3>Communications</h3>
          <ul>
            <li>Send transactional emails (account confirmations, password resets)</li>
            <li>Provide Service updates and important announcements</li>
            <li>Send promotional communications (with your consent, where required)</li>
            <li>Respond to your comments, questions, and requests</li>
          </ul>

          <h3>Security and Legal Compliance</h3>
          <ul>
            <li>Detect, prevent, and address fraud and security issues</li>
            <li>Enforce our Terms of Service and other policies</li>
            <li>Comply with legal obligations and respond to lawful requests</li>
            <li>Protect the rights, property, and safety of Brainy AI and our users</li>
          </ul>

          <h3>Analytics and Research</h3>
          <ul>
            <li>Analyze usage trends and user preferences</li>
            <li>Conduct research to improve educational outcomes</li>
            <li>Generate anonymized statistics and insights</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>4. Legal Basis for Processing (GDPR)</h2>
          <p>
            If you are in the European Economic Area (EEA) or United Kingdom, we process your personal
            data based on the following legal grounds:
          </p>
          <ul>
            <li>
              <strong>Contract Performance:</strong> Processing is necessary to provide the Service
              as agreed in our Terms of Service
            </li>
            <li>
              <strong>Legitimate Interests:</strong> We have legitimate interests in improving the
              Service, ensuring security, and conducting analytics
            </li>
            <li>
              <strong>Consent:</strong> You have given explicit consent for specific processing
              activities (e.g., marketing communications)
            </li>
            <li>
              <strong>Legal Obligation:</strong> Processing is necessary to comply with applicable laws
            </li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>5. How We Share Your Information</h2>
          <p>
            We do not sell your personal information. We may share your information in the following
            circumstances:
          </p>

          <h3>Service Providers</h3>
          <p>
            We share information with third-party service providers who perform services on our behalf:
          </p>
          <ul>
            <li>Cloud hosting providers (e.g., AWS, Azure)</li>
            <li>Payment processors (e.g., Stripe)</li>
            <li>Analytics services (e.g., Google Analytics)</li>
            <li>Email service providers</li>
            <li>Customer support tools</li>
          </ul>
          <p>
            These providers are contractually obligated to protect your information and use it only
            for specified purposes.
          </p>

          <h3>AI Model Providers</h3>
          <p>
            We may share anonymized queries with AI service providers (e.g., OpenAI, Anthropic) to
            generate educational content. These providers process data according to their own privacy
            policies.
          </p>

          <h3>Legal Requirements</h3>
          <p>
            We may disclose your information if required to do so by law or in response to:
          </p>
          <ul>
            <li>Legal processes (subpoenas, court orders)</li>
            <li>Governmental requests</li>
            <li>Protection of our legal rights</li>
            <li>Investigation of fraud or security issues</li>
          </ul>

          <h3>Business Transfers</h3>
          <p>
            If Brainy AI is involved in a merger, acquisition, or sale of assets, your information
            may be transferred to the acquiring entity.
          </p>

          <h3>With Your Consent</h3>
          <p>
            We may share information for any other purpose disclosed to you and with your consent.
          </p>
        </div>

        <div className="legal-section">
          <h2>6. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to:
          </p>
          <ul>
            <li>Provide the Service and maintain your account</li>
            <li>Comply with legal obligations</li>
            <li>Resolve disputes and enforce our agreements</li>
            <li>Achieve legitimate business purposes</li>
          </ul>
          <p>
            Specific retention periods:
          </p>
          <ul>
            <li>
              <strong>Account Data:</strong> Retained while your account is active and for up to
              90 days after deletion (to allow account recovery)
            </li>
            <li>
              <strong>Learning Data:</strong> Retained while your account is active
            </li>
            <li>
              <strong>Usage Logs:</strong> Typically retained for 12-24 months
            </li>
            <li>
              <strong>Payment Records:</strong> Retained for 7 years for tax and accounting purposes
            </li>
            <li>
              <strong>Anonymized Data:</strong> May be retained indefinitely for analytics and AI training
            </li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>7. Your Privacy Rights</h2>
          <p>
            Depending on your location, you may have the following rights regarding your personal data:
          </p>

          <h3>Access and Portability</h3>
          <ul>
            <li>
              <strong>Right to Access:</strong> Request a copy of the personal data we hold about you
            </li>
            <li>
              <strong>Data Portability:</strong> Receive your data in a structured, machine-readable format
            </li>
          </ul>

          <h3>Correction and Deletion</h3>
          <ul>
            <li>
              <strong>Right to Rectification:</strong> Correct inaccurate or incomplete personal data
            </li>
            <li>
              <strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")
            </li>
          </ul>

          <h3>Control and Restriction</h3>
          <ul>
            <li>
              <strong>Right to Restrict Processing:</strong> Limit how we use your personal data
            </li>
            <li>
              <strong>Right to Object:</strong> Object to processing based on legitimate interests
            </li>
            <li>
              <strong>Right to Withdraw Consent:</strong> Withdraw consent for processing based on consent
            </li>
          </ul>

          <h3>Exercising Your Rights</h3>
          <p>
            To exercise these rights, please:
          </p>
          <ul>
            <li>Access your account settings for many self-service options</li>
            <li>Contact us at privacy@brainyai.com for assistance</li>
            <li>Allow up to 30 days for us to respond to your request</li>
          </ul>

          <h3>Complaints</h3>
          <p>
            If you believe we have not adequately addressed your privacy concerns, you have the right
            to lodge a complaint with your local data protection authority.
          </p>
        </div>

        <div className="legal-section">
          <h2>8. Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your personal information:
          </p>

          <h3>Technical Safeguards</h3>
          <ul>
            <li>Encryption of data in transit (TLS/SSL) and at rest</li>
            <li>Secure authentication protocols</li>
            <li>Regular security audits and penetration testing</li>
            <li>Intrusion detection and prevention systems</li>
            <li>Secure software development practices</li>
          </ul>

          <h3>Organizational Safeguards</h3>
          <ul>
            <li>Limited access to personal data on a need-to-know basis</li>
            <li>Employee training on data protection</li>
            <li>Confidentiality agreements with employees and contractors</li>
            <li>Incident response procedures</li>
          </ul>

          <h3>Your Responsibilities</h3>
          <p>
            While we take security seriously, you also play a role in protecting your data:
          </p>
          <ul>
            <li>Use a strong, unique password</li>
            <li>Enable two-factor authentication if available</li>
            <li>Keep your login credentials confidential</li>
            <li>Log out from shared devices</li>
            <li>Report suspicious activity immediately</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>9. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your country
            of residence. These countries may have data protection laws different from your jurisdiction.
          </p>
          <p>
            When we transfer personal data from the EEA or UK to other countries, we ensure appropriate
            safeguards are in place, such as:
          </p>
          <ul>
            <li>Standard Contractual Clauses approved by the European Commission</li>
            <li>Adequacy decisions</li>
            <li>Other legally approved transfer mechanisms</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>10. Children's Privacy</h2>
          <p>
            Our Service is not intended for children under 13 years of age. We do not knowingly collect
            personal information from children under 13. If we become aware that we have collected
            personal data from a child under 13 without parental consent, we will take steps to delete
            that information.
          </p>
          <p>
            For users between 13 and 18, we recommend that parents or guardians supervise their use
            of the Service.
          </p>
          <p>
            If you are a parent or guardian and believe your child has provided us with personal
            information, please contact us at privacy@brainyai.com.
          </p>
        </div>

        <div className="legal-section">
          <h2>11. Cookies and Tracking Technologies</h2>
          <h3>What Are Cookies?</h3>
          <p>
            Cookies are small text files stored on your device that help us provide and improve the
            Service.
          </p>

          <h3>Types of Cookies We Use</h3>
          <ul>
            <li>
              <strong>Essential Cookies:</strong> Necessary for the Service to function (e.g., authentication,
              security)
            </li>
            <li>
              <strong>Functional Cookies:</strong> Remember your preferences and settings
            </li>
            <li>
              <strong>Analytics Cookies:</strong> Help us understand how you use the Service
            </li>
            <li>
              <strong>Advertising Cookies:</strong> Deliver relevant advertisements (if applicable)
            </li>
          </ul>

          <h3>Managing Cookies</h3>
          <p>
            You can control cookies through:
          </p>
          <ul>
            <li>Your browser settings (most browsers allow you to refuse or delete cookies)</li>
            <li>Our cookie consent banner when you first visit the Service</li>
            <li>Third-party opt-out tools for advertising cookies</li>
          </ul>
          <p>
            Note that disabling certain cookies may limit your ability to use some features of the Service.
          </p>
        </div>

        <div className="legal-section">
          <h2>12. Third-Party Links and Services</h2>
          <p>
            Our Service may contain links to third-party websites, applications, or services that are
            not operated by us. This Privacy Policy does not apply to third-party services.
          </p>
          <p>
            We are not responsible for the privacy practices of third parties. We encourage you to
            review the privacy policies of any third-party services you access through our Service.
          </p>
        </div>

        <div className="legal-section">
          <h2>13. California Privacy Rights (CCPA)</h2>
          <p>
            If you are a California resident, you have specific rights under the California Consumer
            Privacy Act (CCPA):
          </p>

          <h3>Right to Know</h3>
          <p>
            You can request information about the personal data we have collected about you in the
            past 12 months, including:
          </p>
          <ul>
            <li>Categories of personal data collected</li>
            <li>Sources of personal data</li>
            <li>Business purposes for collection</li>
            <li>Categories of third parties with whom we share data</li>
          </ul>

          <h3>Right to Delete</h3>
          <p>
            You can request deletion of personal data we have collected from you, subject to certain
            exceptions.
          </p>

          <h3>Right to Opt-Out</h3>
          <p>
            You have the right to opt out of the "sale" of your personal data. We do not sell personal
            data as traditionally defined, but some data sharing may be considered a "sale" under CCPA.
          </p>

          <h3>Non-Discrimination</h3>
          <p>
            We will not discriminate against you for exercising your CCPA rights.
          </p>

          <h3>Exercising Your Rights</h3>
          <p>
            To exercise your CCPA rights, contact us at privacy@brainyai.com or use our online form.
          </p>
        </div>

        <div className="legal-section">
          <h2>14. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices,
            technology, legal requirements, or for other reasons.
          </p>
          <p>
            We will notify you of material changes by:
          </p>
          <ul>
            <li>Posting the updated policy with a new "Last Updated" date</li>
            <li>Sending an email notification (if you have provided an email address)</li>
            <li>Displaying a prominent notice on the Service</li>
          </ul>
          <p>
            Your continued use of the Service after such changes constitutes your acceptance of the
            updated Privacy Policy.
          </p>
        </div>

        <div className="legal-section">
          <h2>15. Contact Us</h2>
          <p>
            If you have questions, concerns, or requests regarding this Privacy Policy or our data
            practices, please contact us:
          </p>
          <div className="contact-info">
            <p>
              <strong>Data Protection Officer:</strong><br />
              Email: privacy@brainyai.com<br />
              Email (Support): brainyai.info@gmail.com<br />
              Website: www.thebrainyai.com
            </p>
          </div>
          <p>
            We will respond to your inquiries within a reasonable timeframe, typically within 30 days.
          </p>
        </div>

        <div className="legal-acknowledgment">
          <p>
            By using Brainy, you acknowledge that you have read and understood this Privacy Policy
            and consent to the collection, use, and disclosure of your information as described herein.
          </p>
        </div>
      </div>

      <div className="legal-page-footer">
        <p>© 2025 Brainy AI. All rights reserved.</p>
        <div className="footer-links">
          <button onClick={() => navigate('/terms')}>Terms of Service</button>
          <span>•</span>
          <button onClick={() => navigate('/privacy')}>Privacy Policy</button>
          <span>•</span>
          <button onClick={() => navigate('/')}>Home</button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

